import { xpToNextLevel, type SavedSkill } from './model.js';
import { V13_MASTERY_MAX_LEVEL } from '../calibration/skill-damage-v13.js';

/**
 * How a saved skill's Mastery rises. Every number here is PROVISIONAL.
 *
 * A skill earns Mastery XP in the character's own XP units, from two things:
 *   - kills: when a monster the character damaged dies, the skill gets the XP the character was
 *     granted for it times the skill's share of the character's damage to that monster;
 *   - uses: a cast that damaged a monster is worth `SKILL_MASTERY_USE_KILL_SHARE` of one melee
 *     kill at the character's level.
 * The XP a Mastery level needs is set so that a skill used regularly from level 1 reaches Mastery
 * 2 when the character is about level 15 and Mastery 10 at about level 100.
 */
export const SKILL_MASTERY_MAX_LEVEL = V13_MASTERY_MAX_LEVEL;

/** Character levels at which a skill used regularly since level 1 reaches Mastery 1, 2, ... 10. */
export const SKILL_MASTERY_CHARACTER_LEVELS: readonly number[] = Object.freeze([1, 15, 26, 36, 47, 58, 68, 79, 89, 100]);

/** What "used regularly" means: the skill earns this share of the character's XP (40% of the damage plus one landed use per kill). */
export const SKILL_MASTERY_REFERENCE_SHARE = 0.5;

/** A cast that damaged a monster is worth this share of one melee kill at the character's level. */
export const SKILL_MASTERY_USE_KILL_SHARE = 0.1;

const xpToReachLevel = (level: number): number => { let total = 0; for (let below = 1; below < level; below += 1) total += xpToNextLevel(below); return total; };

/** Mastery XP needed to go from `masteryLevel` to the next; undefined at the last level. */
export function skillMasteryXpToNext(masteryLevel: number): number | undefined {
  if (!Number.isSafeInteger(masteryLevel) || masteryLevel < 1 || masteryLevel > SKILL_MASTERY_MAX_LEVEL) throw new Error(`mastery level must be between 1 and ${SKILL_MASTERY_MAX_LEVEL} (got ${masteryLevel})`);
  if (masteryLevel === SKILL_MASTERY_MAX_LEVEL) return undefined;
  const from = SKILL_MASTERY_CHARACTER_LEVELS[masteryLevel - 1]!; const to = SKILL_MASTERY_CHARACTER_LEVELS[masteryLevel]!;
  return Math.round(SKILL_MASTERY_REFERENCE_SHARE * (xpToReachLevel(to) - xpToReachLevel(from)));
}

/** The skill after earning `xp` Mastery XP: it rises through as many levels as the XP covers and keeps the rest. */
export function gainSkillMastery(skill: SavedSkill, xp: number): SavedSkill {
  if (!(Number.isFinite(xp) && xp >= 0)) throw new Error('mastery XP gain must be finite and non-negative');
  let masteryLevel = skill.masteryLevel; let masteryXp = (skill.masteryXp ?? 0) + xp;
  for (let needed = skillMasteryXpToNext(masteryLevel); needed !== undefined && masteryXp >= needed; needed = skillMasteryXpToNext(masteryLevel)) { masteryXp -= needed; masteryLevel += 1; }
  if (masteryLevel === SKILL_MASTERY_MAX_LEVEL) masteryXp = 0;
  const { masteryXp: _previous, ...rest } = skill;
  return { ...rest, masteryLevel, ...(masteryXp > 0 ? { masteryXp } : {}) };
}
